<template>
    <Rows-container 
        ref="rowsContainer" 
        class="vod-lines"
        :items-props="partLines"
        @on-down="onDownRows"
        @leave-down="onLeaveDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                :genre="item.genre"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
                @show-all="showAll"
                @plus="plus"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick } from "vue"
    import { useI18n } from "vue-i18n";
    import { Strings } from "@/locales/types/enums";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesVodGenresStore } from "@/stores/LinesVodGenres";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import { useParentControlStore } from "@/stores/ParentControl";
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import type { ExposeInterface } from "./types";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";
    import { useVodPageStore } from "@/stores/VodPage";

    const VodPageStore = useVodPageStore();
    const {
        getCurrentGenre: genre,
    } = storeToRefs(VodPageStore);

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);


    const LinesVodGenresStore = useLinesVodGenresStore();
    const {
        getCardsNewShowsGenres,
        getCardsMoviesGenres,
        getCounterLinesVodGenres
    } = storeToRefs(LinesVodGenresStore);

    const { increaseCounterLinesVodGenres, getCardsSubChannels } = LinesVodGenresStore;

    const emit = defineEmits(['show-all', 'plus', 'list-item-selected', 'ready', 'request-more', 'leave-down']);
    const { t } = useI18n();
    const selectedItem: Ref<IEventSelected | null> = ref(null);
    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesVodGenres";
    const { shelfKeys, VodCounterLines }: typeof AppConstants = AppConstants;

    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.subChannelCards,
                title: '',
                ref: shelfKeys.subChannelCards,
                lineId: shelfKeys.subChannelCards,
                skeleton: SkeletonName.TABSGENRES_CARD_SKELETON,
                genre: genre.value,
                cardItems: getCardsSubChannels(genre.value)
            },
            {
                keyData: shelfKeys.newShowsCards,
                title: t(Strings.VOD_NEW_RELEASES).toString(),
                ref: shelfKeys.newShowsCards,
                lineId: shelfKeys.newShowsCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                genre: genre.value,
                cardItems: getCardsNewShowsGenres.value
            },
            {
                keyData: shelfKeys.moviesCards,
                title: t(Strings.VOD_MOVIES).toString(),
                ref: shelfKeys.moviesCards,
                lineId: shelfKeys.moviesCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                genre: genre.value,
                cardItems: getCardsMoviesGenres.value
            }
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesVodGenres.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };

    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesVodGenres.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= VodCounterLines) {
            const isIncreasing = (counter.value % VodCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesVodGenres();
                counter.value++;
            }
        }
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const onLeaveDownRows = (): void => {
       emit("leave-down");
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    }; 

    const onDownRows = (): void => {
        if (getCounterLinesVodGenres.value < allLines.value.length) {
            increaseCounterLinesVodGenres();
        }
    };

    const showAll = (event: IEventSelected): void => {
        emit("show-all", event);
    };
    const plus = (lineId: string): void => {
        emit("plus", lineId);
    };
    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };
    const isEmpty = (): boolean => rowsContainer.value!.isEmpty();
    const getCountLoadedRows = (): number | null => rowsContainer.value ? rowsContainer.value.getCountLoadedRows() : null;
    const focusLastRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusLastRow();
        }
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow,
        focusLastRow,
        getCountLoadedRows,
        isEmpty
    });
</script>